import { MutationCache, QueryClient } from '@tanstack/react-query';

import { toast } from '@/hooks/useToast';
import { i18n, type TranslationKey } from '@/i18n';

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      /** The locale key of the toast that a failure shows. It defaults to `common.saveFailed`. */
      errorKey?: TranslationKey;
      /** False when every caller shows the failure itself. */
      toastsError?: boolean;
    };
  }
}

/** Returns the app's query client: the query defaults, and one error toast per failed mutation.
 *  @param retry The number of retries of a failed query, or false for none. */
export function createQueryClient({ retry = 1 }: { retry?: number | false } = {}): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: { staleTime: 30_000, retry, refetchOnWindowFocus: false },
    },
    mutationCache: new MutationCache({
      onError: (_error, _variables, _onMutateResult, mutation) => {
        if (mutation.meta?.toastsError === false) return;
        toast({ variant: 'error', message: i18n.t(mutation.meta?.errorKey ?? 'common.saveFailed') });
      },
    }),
  });
}

/** Puts one mutation response into its list and replaces the row with the same id. */
export function echoIntoList<T extends { id: string }>(
  client: QueryClient,
  queryKey: readonly unknown[],
  row: T | null,
) {
  if (!row) return;
  client.setQueriesData<T[]>({ queryKey }, (rows = []) => [
    ...rows.filter((existing) => existing.id !== row.id),
    row,
  ]);
}

/** Takes one row out of the list it belonged to. */
export function dropFromList<T extends { id: string }>(
  client: QueryClient,
  queryKey: readonly unknown[],
  id: string,
) {
  client.setQueriesData<T[]>({ queryKey }, (rows = []) => rows.filter((row) => row.id !== id));
}
